import { FormEvent, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { LoadingComp } from "../../components/loading/loading.comp";
import { useData } from "../../libs/data/app-data.hook";
import { useRegistration } from "../../libs/registration/registration.hook";
import { RegistrationContainer } from "./styles/registration-container.style";
import { SingleInput } from "../../components/form/styles/single-input";
import { AppForm } from "../../components/form/styles/app-form";
import { StudentInfoComp } from "../../components/student-info/student-info.comp";
import { TermsV2Comp } from "../../components/terms/terms-v2.comp";
import { CtaButton } from "../../components/form/styles/cta-button";

const dayNames = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
];

export const PreregisterView = () => {
  const { eventId } = useParams();
  const { loading, activities } = useData();
  const {
    processing,
    registrationInfo,
    setRegistrationInfo,
    sendRegistration,
  } = useRegistration();
  const currentEvent = useMemo(
    () => (activities?.all ? activities.all[`${eventId}`] : {}),
    [eventId, activities]
  );
  const [showThanks, setShowThanks] = useState(false);
  const isSchedule = currentEvent && currentEvent.tags?.includes("schedule");
  const isRegular = currentEvent && currentEvent.tags?.includes("regular");
  const isAcademic = currentEvent && currentEvent.tags?.includes("academic");
  const isSession = currentEvent && currentEvent.tags?.includes("session");
  const isPencil = currentEvent && currentEvent.tags?.includes("pencil");
  const isAcrylic = currentEvent && currentEvent.tags?.includes("acrylic");

  const isEmail =
    registrationInfo.email?.includes(".") &&
    registrationInfo.email?.includes("@");

  const isMonthly = currentEvent.type === "schedule";

  const canProceed = useMemo(
    () =>
      registrationInfo.students.participant?.firstName &&
      registrationInfo.students.participant?.lastName &&
      registrationInfo.students.participant?.dateOfBirth &&
      registrationInfo.firstName &&
      registrationInfo.lastName &&
      registrationInfo.phone &&
      registrationInfo.email &&
      registrationInfo.liabilitySigned &&
      registrationInfo.paymentSigned &&
      registrationInfo.returnsSigned &&
      registrationInfo.photoReleaseSigned &&
      registrationInfo.medicalSigned &&
      registrationInfo.codeSigned &&
      isEmail &&
      (!isMonthly || (isMonthly && registrationInfo.cancellationSigned)),
    [registrationInfo]
  );

  useEffect(() => {
    setRegistrationInfo({
      event: eventId,
    });
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (canProceed) {
      try {
        await sendRegistration();
        if (currentEvent.payment) {
          window.location.href = currentEvent.payment;
        } else {
          setShowThanks(true);
        }
      } catch (err) {
        alert("Registration failed. Please try again.");
      }
    }
  };

  const onAddFood = (event: FormEvent) => {
    setRegistrationInfo({
      foodOption: !registrationInfo.foodOption ? "yes" : "",
    });
  };

  const onAddExtendedHours = (event: FormEvent) => {
    setRegistrationInfo({
      extendedHours: !registrationInfo.extendedHours ? "yes" : "",
    });
  };

  if (showThanks)
    return (
      <RegistrationContainer>
        <AppForm>
          <h1>
            Thank you for Pre-Registering!
            <br />
            <strong>{currentEvent?.name ? `${currentEvent.name}` : ""}</strong>
            <br />
            {currentEvent && (
              <>
                <small>
                  {dayNames[currentEvent.day]} | {currentEvent.startTime} -{" "}
                  {currentEvent.endTime} | {currentEvent.description}
                </small>
              </>
            )}
          </h1>
        </AppForm>
      </RegistrationContainer>
    );

  return (
    <RegistrationContainer>
      <AppForm onSubmit={onSubmit}>
        <h1>
          Pre-Registration for{" "}
          <strong>{currentEvent?.name ? `${currentEvent.name}` : ""}</strong>
          <br />
          {currentEvent && (
            <>
              <small>
                {dayNames[currentEvent.day]} | {currentEvent.startTime} -{" "}
                {currentEvent.endTime} | {currentEvent.description}
              </small>
              <br />
              <br />
              {currentEvent.earlyBirdPrice ? (
                <>
                  <small
                    style={{
                      textDecoration: "line-through",
                      color: "#666666",
                    }}
                  >
                    Price: {currentEvent.price}
                  </small>
                  <br />
                  <span style={{ color: "#278664" }}>
                    <small>Early bird:</small> {currentEvent.earlyBirdPrice}
                  </span>
                </>
              ) : (
                <>
                  {currentEvent.price && (
                    <small>Price: {currentEvent.price}</small>
                  )}
                </>
              )}
            </>
          )}
        </h1>

        {!isSession && (
          <div className="text-left">
            <section>
              <h2>
                🎉 Thank you for your interest in Pre-Registering with
                Colourinspired Academy!
              </h2>
              <p>
                We’re excited to welcome your child to our creative community!
              </p>
            </section>

            {isRegular && (
              <section>
                <h2>What are Multimedia Art Classes?</h2>
                <p>
                  At Colour Inspired Academy, our Multimedia Art Classes give
                  kids the chance to try many different kinds of art — all in
                  one program!
                </p>
                Each week, children explore fun activities like:
                <ul>
                  <li>🎨 Painting</li>
                  <li>✏️ Drawing</li>
                  <li>🖍️ Collage</li>
                  <li>🧱 Clay and sculpture</li>
                  <li>
                    🖼️ Mixed media (using more than one material in a project)
                  </li>
                </ul>
                These classes are great for helping kids:
                <ul>
                  <li>✅ Build creativity and confidence</li>
                  <li>✅ Improve focus and fine motor skills</li>
                  <li>✅ Express themselves in healthy, fun ways</li>
                  <li>
                    ✅ Learn to experiment and enjoy the process — not just the
                    final result
                  </li>
                </ul>
                <p>
                  No experience is needed. All materials are included, and every
                  child goes home with their own unique artwork.
                </p>
                <p>
                  It’s a fun, relaxed space where kids can be creative, get a
                  little messy, and feel proud of what they make!
                </p>
              </section>
            )}

            {isAcademic && (
              <section>
                <h2>🎓 Academic Art Education</h2>
                <p>
                  By registering, your child is enrolling in an Academic Art
                  Education program — a more focused and in-depth training
                  designed for students who are ready to take their artistic
                  skills to the next level.
                </p>
                In this program, students will:
                <ul>
                  <li>Learn advanced art techniques</li>
                  <li>Develop strong sketching and concept planning skills</li>
                  <li>
                    Study the foundations of art history and its influence on
                    modern creativity
                  </li>
                  <li>
                    Build professional-level portfolios and participate in
                    critique sessions
                  </li>
                </ul>
                <p>
                  Participation in all Colourinspired exhibitions is mandatory,
                  including our signature event:
                  <br />
                  “I Can Change the World” Art Contest — a powerful platform for
                  young artists to share their voice and vision with the
                  community.
                </p>
              </section>
            )}

            <section>
              <h2>Registration Confirmation</h2>
              <p>
                This pre-registration secures your child’s spot in the selected
                class for <strong>September 2025</strong> and locks in the{" "}
                <strong>early bird discount</strong> — available for a limited
                time only.
              </p>
            </section>

            <section>
              <h2>✅ What Happens Next</h2>
              <p>
                After completing the registration form, you’ll receive a
                confirmation email confirming your child’s placement in the
                chosen class.
              </p>
            </section>

            <section>
              <h2>💳 Payment & Membership Info</h2>
              <ul>
                <li>
                  Classes run on a <strong>monthly subscription</strong>.
                </li>
                <li>
                  You will receive a <strong>payment link on August 25</strong>.
                </li>
                <li>
                  You have until <strong>August 31</strong> to complete your
                  first payment (covers September 2025).
                </li>
                <li>
                  Subsequent payments will be{" "}
                  <strong>automatically processed monthly</strong> on the same
                  date as your first payment.
                </li>
                <li>
                  <em>Example:</em> If you pay on August 28, your next payment
                  will be on September 28 (for October).
                </li>
                <li>
                  Classes run <strong>September to June</strong>. Membership
                  will be paused for July and August.
                </li>
              </ul>
            </section>

            <section>
              <h2>🎨 Bonus for Members</h2>
              <p>
                Active members get <strong>FREE access</strong> to our{" "}
                <strong>Colourinspired Art Club</strong> every{" "}
                <strong>Wednesday from 6:30–8:00 PM</strong>!
              </p>
            </section>

            <section>
              <h2>🔔 Please Note</h2>
              <p>
                If you cancel your subscription, the early bird promotion will{" "}
                <strong>no longer apply</strong> if you re-enroll later.
              </p>
            </section>

            <section>
              <h2>📞 Questions?</h2>
              <p>If you have any questions, feel free to contact us:</p>
              <ul>
                <li>
                  <strong>Anna (Administrator):</strong> 647-927-5083
                </li>
                <li>
                  <strong>Jane (Director):</strong> 647-236-6647
                </li>
              </ul>
              <p>We’re happy to help!</p>
            </section>
          </div>
        )}

        <h2>Child/Participant Information</h2>
        <StudentInfoComp
          id="participant"
          showSelection={false}
          showOptional={true}
        />

        {(currentEvent?.showFoodOption ||
          currentEvent?.showExtendedHoursOption) && <h2>Camp Options</h2>}
        <div className="text-left">
          {currentEvent?.showFoodOption && (
            <p>
              <input
                type="checkbox"
                checked={registrationInfo.foodOption === "yes"}
                onChange={onAddFood}
              />{" "}
              Add food option
            </p>
          )}
          {currentEvent?.showExtendedHoursOption && (
            <p>
              <input
                type="checkbox"
                checked={registrationInfo.extendedHours === "yes"}
                onChange={onAddExtendedHours}
              />{" "}
              Add extended hours option
            </p>
          )}
        </div>

        <h2>Parent / Guardian Information</h2>
        <SingleInput
          onChange={(event: any) =>
            setRegistrationInfo({ firstName: event.target.value })
          }
          value={registrationInfo.firstName}
          placeholder="First name"
        />
        <SingleInput
          onChange={(event: any) =>
            setRegistrationInfo({ lastName: event.target.value })
          }
          value={registrationInfo.lastName}
          placeholder="Last name"
        />
        <SingleInput
          onChange={(event: any) =>
            setRegistrationInfo({ email: event.target.value })
          }
          value={registrationInfo.email}
          placeholder="Email"
        />
        <SingleInput
          onChange={(event: any) =>
            setRegistrationInfo({ phone: event.target.value })
          }
          value={registrationInfo.phone}
          placeholder="Phone"
        />

        <h2>Terms and Conditions</h2>
        <TermsV2Comp isMonthly={isMonthly} />

        <CtaButton disabled={!canProceed}>Register</CtaButton>
        <ul className="error-summary">
          {!registrationInfo.students.participant?.firstName ? (
            <li>Participant First Name is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.students.participant?.lastName ? (
            <li>Participant Last Name is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.students.participant?.dateOfBirth ? (
            <li>Participant Date of Birth is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.firstName ? <li>First Name is required</li> : ""}
          {!registrationInfo.lastName ? <li>Last Name is required</li> : ""}
          {!registrationInfo.phone ? <li>Phone is required</li> : ""}
          {!isEmail ? <li>Email is required</li> : ""}
          {!registrationInfo.liabilitySigned ? (
            <li>Liability Waiver is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.paymentSigned ? (
            <li>Payment agreement is required</li>
          ) : (
            ""
          )}
          {isMonthly && !registrationInfo.cancellationSigned ? (
            <li>Cancellation fee agreement is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.returnsSigned ? (
            <li>Return Policy is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.photoReleaseSigned ? (
            <li>Photo Release is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.medicalSigned ? (
            <li>Allergies or Medical Conditions are required</li>
          ) : (
            ""
          )}
          {!registrationInfo.codeSigned ? (
            <li>Code of Conduct is required</li>
          ) : (
            ""
          )}
        </ul>
        <br />
        <br />
      </AppForm>
      {(loading || processing) && <LoadingComp />}
    </RegistrationContainer>
  );
};
